import * as React from 'react';
import { ChevronsUpDown } from 'lucide-react';

import { computeInitials } from '@klareospace/pharos-lib-utils';

import { cn } from '../../lib/utils';

export interface SidebarUserCardProps {
  /** Optional classes for the avatar circle — customize background color etc. */
  avatarClassName?: string;
  /** Optional extra classes for the outer wrapper button */
  className?: string;
  /** Fallback initials rendered when name is empty or falsy (defaults to '??') */
  fallbackInitials?: string;
  /** User display name */
  name: string;
  /** Role or subtitle text (e.g. "Account Manager") */
  subtitle?: string;
}

export const SidebarUserCard = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<'button'> & SidebarUserCardProps
>(({ avatarClassName, className, fallbackInitials = '??', name, subtitle, ...buttonProps }, ref): React.JSX.Element => {
  const initials = computeInitials(name, fallbackInitials);

  return (
    <button
      {...buttonProps}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg bg-sidebar-accent/30 px-3 py-3 text-start transition-colors hover:bg-sidebar-accent/50 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0',
        className,
      )}
      ref={ref}
      type="button"
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white',
          avatarClassName,
        )}
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
        <p className="truncate text-sm font-medium">{name}</p>
        {subtitle && <p className="truncate text-xs text-sidebar-foreground/60">{subtitle}</p>}
      </div>
      <ChevronsUpDown className="size-4 shrink-0 text-sidebar-foreground/60 group-data-[collapsible=icon]:hidden" />
    </button>
  );
});

SidebarUserCard.displayName = 'SidebarUserCard';
